import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import React, { useState } from "react";
import { WorkflowLockUnlockModal } from "~/components/common/company_workflow_settings/WorkflowLockUnlockModal";
import { WorkflowStartModal } from "~/components/common/company_workflow_settings/WorkflowStartModal";
import { ConstructionConfirmedFooter } from "~/components/common/constructions/ConstructionConfirmedFooter";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionDownloadButtons } from "~/components/common/constructions/top/ConstructionDownloadButtons";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { useDealEstimationHeaders } from "~/hooks/deals/useDealEstimationHeaders";
import { useDealEstimationHistories } from "~/hooks/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/deals/useDealEstimations";
import { useDealWorkflowRestriction } from "~/hooks/deals/useDealWorkflowRestriction";
import { useCompanyWorkflowSettings } from "~/hooks/useCompanyWorkflowSettings";
import { useDealConstructionItems } from "~/hooks/useDealConstructionItems";
import { useDeals } from "~/hooks/useDeals";
import useItandiAccount from "~/hooks/useItandiAccount";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import type { NskResponse } from "~/models/Api";
import {
	canStartWorkflow,
	WORKFLOW_LOCK_TARGET_LABEL,
	WORKFLOW_RESOURCE_TYPE
} from "~/models/CompanyWorkflowSetting";
import { estimationHeaderStatus } from "~/models/Deal";
import type {
	DealConstruction,
	DealConstructionContext
} from "~/models/DealConstruction";
import { estimationRequestSide } from "~/models/DealEstimationHeader";
import { canEditConstructionTarget } from "~/models/DealVendorCompany";
import { buildWorkflowLockMessage } from "~/models/DealWorkflowRestriction";
import {
	IN_CONSTRUCTION,
	IN_ESTIMATION,
	isOwnStatus,
	OWN_SEND_ESTIMATE,
	SEND_ESTIMATE
} from "~/models/Status";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { PATH as ESTIMATIONS_EDIT_PATH } from "~/pages/deals/[id]/estimations/edit";
import { update as updateStatus } from "~/utils/api/DealStatus";
import { update as updateEnabled } from "~/utils/api/deals/DealEstimation";
import { getDownloadPath } from "~/utils/api/deals/DealEstimationFile";
import {
	updateAccept,
	updateReject
} from "~/utils/api/deals/DealEstimationHeader";
import { getDownloadPath as getHistoryDownloadPath } from "~/utils/api/deals/DealEstimationHistoryFile";
import { isEmpty } from "~/utils/StringUtil";
import Styles from "./Page.module.scss";

// 下記ステータスの場合のみ施工対象を変更できる
const allowedStatuses: number[] = [SEND_ESTIMATE, OWN_SEND_ESTIMATE];

export function Page() {
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	const router = useRouter();
	const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
	const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
	const { data: currentAccount } = useItandiAccount().currentAccount();
	const { data: companyWorkflowSettings } = useCompanyWorkflowSettings();
	const { data: estimationContext, mutate: mutateConstructions } =
		useDealEstimations().index(id);
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const {
		data: workflowRestriction,
		error: workflowRestrictionError,
		isLoading: isWorkflowRestrictionLoading,
		mutate: mutateWorkflowRestriction
	} = useDealWorkflowRestriction(id);
	const { data: dealConstructionItems } = useDealConstructionItems();
	const { data: dealEstimationHistoryContext } =
		useDealEstimationHistories().index(id);
	const { data: dealEstimationHeaders, mutate: mutateHeaders } =
		useDealEstimationHeaders().index(id);
	const requestHeader =
		dealEstimationHeaders?.find((header) => header.status === "request") ??
		null;
	const { data: dealEstimationRequestHistoryContext, isLoading } =
		useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
	const isReadonly = React.useMemo(() => {
		if (deal === null) {
			return true;
		}
		if (requestHeader !== null) return true;
		return !allowedStatuses.some((s) => s === deal.statusId);
	}, [deal, requestHeader]);
	React.useEffect(() => {
		if (deal === null) return;
		if (!isOwnStatus(deal.statusId) && estimationRequestSide(deal, "client")) {
			router.replace(ESTIMATIONS_EDIT_PATH(deal.id)).then();
		}
	}, [deal, router]);
	if (
		deal === null ||
		isLoading ||
		estimationContext === null ||
		dealEstimationHistoryContext === null ||
		dealConstructionItems === null ||
		dealEstimationHeaders === null ||
		currentAccount === null
	) {
		return <Loading isLoading />;
	}
	const getConstructionContextList = (): DealConstructionContext => {
		if (isOwnStatus(deal.statusId)) {
			if (
				dealEstimationHistoryContext &&
				dealEstimationHistoryContext?.dealConstructions.length !== 0
			) {
				return dealEstimationHistoryContext;
			}
			return estimationContext;
		}
		return dealEstimationRequestHistoryContext ?? estimationContext;
	};
	const handleMutateConstructions = async (): Promise<void> => {
		await mutateConstructions?.();
		await mutateHeaders?.();
		await mutateDeal?.();
		// 施工対象の切り替えでワークフローを開始できるかが変わるため取り直す
		await mutateWorkflowRestriction();
	};
	const handleClickPreview = (): void => {
		const path = getDownloadPath(id);
		window.open(path);
	};
	const handleClickHistoryPreview = (): void => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		const path = getHistoryDownloadPath(deal.id, requestHeader.id);
		window.open(path);
	};
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "見積り確認" }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];
	const handleUpdateEnabled = async (
		newConstruction: DealConstruction
	): Promise<void> => {
		if (newConstruction.type !== "DealEstimation") {
			return;
		}
		await updateEnabled(newConstruction, newConstruction.id, deal.id);
	};
	const handleAcceptUpdateStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return updateAccept(deal.id, requestHeader.id);
	};
	const handleUpdateRejectStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return updateReject(deal.id, requestHeader.id);
	};
	const handleAcceptForward = async (): Promise<NskResponse> =>
		updateStatus(deal.id, IN_CONSTRUCTION, null, null);
	const isEstimationDocumentDownloadable =
		!isWorkflowRestrictionLoading &&
		!workflowRestrictionError &&
		(workflowRestriction?.estimation.downloadable ?? false);
	const isEstimationLocked =
		isWorkflowRestrictionLoading ||
		!!workflowRestrictionError ||
		(workflowRestriction?.estimation.locked ?? true);
	const workflowLockMessage =
		isWorkflowRestrictionLoading ||
		workflowRestrictionError ||
		workflowRestriction === null
			? null
			: buildWorkflowLockMessage(
					workflowRestriction.estimation,
					WORKFLOW_LOCK_TARGET_LABEL[WORKFLOW_RESOURCE_TYPE.ESTIMATION]
				);
	const canEditConstructionEnabled = (): boolean =>
		canEditConstructionTarget(deal) && !isEstimationLocked;
	const estimationNote = requestHeader?.note ?? estimationContext.note;
	const estimationStatus = estimationHeaderStatus(deal);

	return (
		<DefaultV2>
			<ClientDealLayout deal={deal} enableChat items={items}>
				<Flex justifyContent="space-between">
					<Flex alignItems="center" gap={designTokens["spacing/s"]}>
						<h1 className={Styles.EstimateTitle}>見積り確認</h1>
						{estimationStatus.label !== "" &&
							estimationStatus.variant !== null && (
								<PresentationChip variant={estimationStatus.variant}>
									{estimationStatus.label}
								</PresentationChip>
							)}
					</Flex>
					{!isSp ? (
						<Flex gap={designTokens["spacing/xs"]}>
							<ConstructionDownloadButtons
								constructionContext={estimationContext}
								dealEstimationDraftHeader={null}
								dealEstimationHistoryContext={
									dealEstimationRequestHistoryContext ?? null
								}
								disabled={!isEstimationDocumentDownloadable}
								handleClickEstimationHistoryPreview={
									requestHeader ? handleClickHistoryPreview : null
								}
								handleClickEstimationPreview={handleClickPreview}
								ownManagement={isOwnStatus(deal.statusId)}
							/>
							{canStartWorkflow(
								companyWorkflowSettings,
								WORKFLOW_RESOURCE_TYPE.ESTIMATION
							) ? (
								<Button
									disabled={
										isWorkflowRestrictionLoading ||
										!!workflowRestrictionError ||
										!workflowRestriction?.estimation.startable
									}
									onClick={() => {
										setIsWorkflowModalOpen(true);
									}}
									variant="primary"
								>
									ワークフローを開始
								</Button>
							) : null}
							{workflowRestriction?.estimation.locked ? (
								<Button
									disabled={!workflowRestriction.estimation.unlockable}
									onClick={() => {
										setIsUnlockModalOpen(true);
									}}
									variant="secondary"
								>
									編集ロックを解除
								</Button>
							) : null}
						</Flex>
					) : null}
				</Flex>
				{estimationHeaderStatus(deal).description !== "" &&
					deal.statusId !== IN_ESTIMATION && (
						<Box width="fit-content">
							<Alertv2 variant="warning">
								<Paragraph>
									{estimationHeaderStatus(deal).description}
								</Paragraph>
							</Alertv2>
						</Box>
					)}
				<Box mt={designTokens["spacing/xs"]} width="fit-content">
					<Alertv2 variant="info">
						<Paragraph>
							{canEditConstructionEnabled() ? (
								<>
									施工対象のスイッチのオン/オフで、各小計と合計金額が再計算されます。ご希望の内容に調整した見積書をダウンロードできます。
									<br />
								</>
							) : null}
							提案ラベルがついた施工項目は、ダウンロード後の見積書の施工項目名に【ご提案】が付いた状態で出力されます。
						</Paragraph>
					</Alertv2>
				</Box>
				{workflowLockMessage !== null ? (
					<Box mt={designTokens["spacing/xs"]} width="fit-content">
						<Alertv2 variant="warning">
							<Paragraph>{workflowLockMessage}</Paragraph>
						</Alertv2>
					</Box>
				) : null}
				<ConstructionTop
					constructionContext={estimationContext}
					dealEstimationHistoryContext={dealEstimationRequestHistoryContext}
					ownManagement={isOwnStatus(deal.statusId)}
					readonly={isReadonly}
					showSubtotal
				/>
				<ConstructionList
					companyType="client"
					constructionContext={getConstructionContextList()}
					deal={deal}
					dealConstructionItems={dealConstructionItems}
					docType="estimation"
					handleMutateConstructions={handleMutateConstructions}
					handleUpdateEnabled={
						canEditConstructionEnabled() ? handleUpdateEnabled : null
					}
					readonly={isReadonly}
					showEstimationConfirmedButton={
						(requestHeader && !isOwnStatus(deal.statusId)) ?? false
					}
					showHighlight={!isOwnStatus(deal.statusId)}
				/>
				{deal.estimationBy === "vendor" && (
					<Box mt={designTokens["spacing/l"]}>
						<Panel>
							<PanelHeader title="備考" />
							<div
								className={
									requestHeader?.noteModified ? Styles.ModifiedNote : undefined
								}
							>
								<PanelBody>
									{isEmpty(estimationNote) && requestHeader?.noteModified
										? "備考が削除されました。"
										: estimationNote}
								</PanelBody>
							</div>
						</Panel>
					</Box>
				)}
				<ConstructionConfirmedFooter
					acceptRejectDisabled={isEstimationLocked}
					account={currentAccount}
					companyType="client"
					deal={deal}
					dealEstimationContext={estimationContext}
					dealEstimationHeader={requestHeader}
					forwardDisabled={
						isWorkflowRestrictionLoading ||
						!!workflowRestrictionError ||
						!workflowRestriction?.canForwardToInConstruction
					}
					handleAcceptForward={handleAcceptForward}
					handleMutate={handleMutateConstructions}
					handleUpdateAcceptStatus={handleAcceptUpdateStatus}
					handleUpdateRejectStatus={handleUpdateRejectStatus}
					isAdmin={currentAccount.isAdmin}
				/>
				{isUnlockModalOpen ? (
					<WorkflowLockUnlockModal
						dealId={deal.id}
						handleClose={() => {
							setIsUnlockModalOpen(false);
						}}
						resourceType={WORKFLOW_RESOURCE_TYPE.ESTIMATION}
					/>
				) : null}
				{isWorkflowModalOpen ? (
					<WorkflowStartModal
						companyWorkflowSettings={companyWorkflowSettings!}
						dealId={deal.id}
						handleClose={() => {
							setIsWorkflowModalOpen(false);
						}}
						resourceType={WORKFLOW_RESOURCE_TYPE.ESTIMATION}
					/>
				) : null}
			</ClientDealLayout>
		</DefaultV2>
	);
}
